class DatetimeInputElement extends HTMLElement {
    static formAssociated = true;

    private internals: ElementInternals;
    private input: HTMLInputElement;

    static get observedAttributes() {
        return [
            "value",
            "required",
            "placeholder",
            "name",
            "disabled",
            "class",
        ];
    }

    constructor() {
        super();
        this.internals = this.attachInternals();

        this.input = document.createElement("input");
        this.input.type = "datetime-local";

        this.input.addEventListener("input", () => this.syncFormValue());
        this.input.addEventListener("change", () => this.syncFormValue());
    }

    connectedCallback() {
        if (!this.input.isConnected) {
            this.appendChild(this.input);
        }
        // Pick up any class set before upgrade (e.g. via template string)
        if (this.hasAttribute("class")) {
            this.input.className = this.getAttribute("class") ?? "";
            this.removeAttribute("class");
        }
        this.syncFormValue();
    }

    attributeChangedCallback(name: string, oldValue: string, newValue: string) {
        if (oldValue === newValue) return;

        switch (name) {
            case "class":
                if (newValue !== null) {
                    this.input.className = newValue;
                    this.removeAttribute("class"); // triggers this callback again with newValue === null, which we guard above
                }
                break;
            case "value":
                this.input.value = toDatetimeLocal(newValue);
                this.syncFormValue();
                break;
            case "required":
                this.input.required = newValue !== null;
                break;
            case "placeholder":
                this.input.placeholder = newValue ?? "";
                break;
            case "disabled":
                this.input.disabled = newValue !== null;
                break;
        }
    }

    private syncFormValue() {
        const rfc3339 = toRFC3339(this.input.value);
        this.internals.setFormValue(rfc3339 ?? "");
        if (this.input.required) {
            this.internals.setValidity(
                this.input.validity,
                this.input.validationMessage,
                this.input,
            );
        } else {
            this.internals.setValidity({});
        }
    }

    get form() {
        return this.internals.form;
    }
    get name() {
        return this.getAttribute("name");
    }
    get validity() {
        return this.internals.validity;
    }
    get validationMessage() {
        return this.internals.validationMessage;
    }
    checkValidity() {
        return this.internals.checkValidity();
    }
    reportValidity() {
        return this.internals.reportValidity();
    }
}

function toRFC3339(localDateTimeStr: string): string | null {
    if (!localDateTimeStr) return null;
    const d = new Date(localDateTimeStr);
    if (isNaN(d.getTime())) return null;

    const pad = (n: number) => String(n).padStart(2, "0");
    const tzOffsetMin = -d.getTimezoneOffset();
    const sign = tzOffsetMin >= 0 ? "+" : "-";
    const offH = pad(Math.floor(Math.abs(tzOffsetMin) / 60));
    const offM = pad(Math.abs(tzOffsetMin) % 60);

    return `${localDateTimeStr}:00${sign}${offH}:${offM}`;
}

function toDatetimeLocal(value: string): string {
    if (!value) return "";
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
        d.getHours(),
    )}:${pad(d.getMinutes())}`;
}

customElements.define("bd-datetime-input", DatetimeInputElement);
